import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import LogoMark from '../components/LogoMark';
import ServerSettings from '../components/ServerSettings';
import RegisterPanel, { ResendVerification, waitMinutes } from '../components/RegisterPanel';
import GoogleSignInButton, { googleSignInAvailable } from '../components/GoogleSignInButton';
import { srOnly } from '../components/organic/motion';
import { needsServerChoice } from '../utils/serverConfig';
import { PRIVACY_POLICY_URL, isExternalPolicy } from '../utils/privacy';
import { clearWorkspace } from '../utils/workspace';

export default function Login() {
  const { t, i18n }             = useTranslation('login');
  const { t: tReg }             = useTranslation('register');
  const { t: tReset }           = useTranslation('passwordReset');
  const [mode, setMode]         = useState('signin'); // 'signin' | 'register'
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]             = useState('');
  // After a wrong email or password, where this page has a Google button: a
  // pointer to it, for someone whose account signs in with Google. Shown for
  // every 401 alike, so it says nothing about whether the address has an
  // account (public auth routes never tell, CLAUDE.md Task 2.3).
  const [googleHint, setGoogleHint]   = useState(false);
  const [googleLit, setGoogleLit]     = useState(false);
  const googleArea                    = useRef(null);
  // The highlight's timer is cleared on unmount: firing after the page has
  // gone sets state on nothing (and, in tests, after the DOM is torn down).
  const googleLitTimer                = useRef(null);
  useEffect(() => () => clearTimeout(googleLitTimer.current), []);
  // Set when the server says the address is not verified yet, so the person
  // can ask for a new link right where the refusal appeared.
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [loading, setLoading]   = useState(false);
  // Google sign-in. A credential is kept only between the server's "accept
  // the privacy policy first" and the person's answer, and only in memory:
  // never in storage or the address bar.
  const [googleBusy, setGoogleBusy]         = useState(false);
  const [googleError, setGoogleError]       = useState('');
  const [consentFor, setConsentFor]         = useState(null);
  const [googleAccepted, setGoogleAccepted] = useState(false);
  const [googlePending, setGooglePending]   = useState(false);
  // The Google popup closes onto a step that was not on screen when it
  // opened. Focus goes to it, and a live region that is always there says it:
  // a region inserted along with its text is often not read at all.
  const consentRef = useRef(null);
  const pendingRef = useRef(null);
  const [googleStep, setGoogleStep] = useState('');
  const { t: tAccount }         = useTranslation('account');
  // Opens by itself when a native build has no server to talk to yet --
  // signing in cannot work before one is chosen.
  const [serverOpen, setServerOpen] = useState(() => needsServerChoice());
  const accountDeleted = new URLSearchParams(window.location.search).get('accountDeleted') === '1';
  const { login, user }         = useAuth();
  const navigate                = useNavigate();
  // Where PrivateRoute was sending the person before it asked them to sign
  // in. Router state, never a query parameter, so it cannot point off-site.
  const from = useLocation().state?.from;
  const target = from?.pathname ? `${from.pathname}${from.search || ''}${from.hash || ''}` : '/';

  const toggleLanguage = (lng) => {
    if (lng !== i18n.language) i18n.changeLanguage(lng);
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setGoogleHint(false);
    setUnverifiedEmail('');
    setGoogleError('');
    setConsentFor(null);
    setGoogleAccepted(false);
    setGooglePending(false);
    setGoogleStep('');
  };

  // The one way into the app from here, whichever way the person proved who
  // they are: the same storage and the same redirect. A sign-in forgets the
  // workspace this device last chose, so Home asks again (Field or Lab) right
  // after it. Only here: not in AuthContext.login, which a voluntary password
  // change also calls, and not on a restored session or a logout.
  const enter = (token) => {
    clearWorkspace();
    login(token);
    navigate(target, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setGoogleHint(false);
    setUnverifiedEmail('');
    setGoogleError('');
    setConsentFor(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      enter(res.data.token);
    } catch (err) {
      // Error CODES, not the server's English sentences: the UI is Hebrew by
      // default. The server only reveals these after the password matched.
      const code = err.response?.data?.code;
      if (code === 'email_not_verified') {
        setError(tReg('notVerified'));
        setUnverifiedEmail(email.trim());
      } else if (code === 'rate_limited') {
        setError(tReg('errors.rate_limited', { minutes: waitMinutes(err.response.data) }));
      } else if (code === 'account_inactive') {
        setError(tReg('accountInactive'));
      } else if (err.response?.status === 401) {
        setError(t('wrongCredentials'));
        googleSignInAvailable().then(setGoogleHint).catch(() => setGoogleHint(false));
      } else if (err.response?.status === 426) {
        // The app and the server are on different versions (a deploy in
        // progress); utils/versionSkew.js put the reader's-language reason here.
        setError(err.response.data?.error || t('loginFailed'));
      } else if (!err.response) {
        setError(t('serverUnreachable'));
      } else {
        setError(t('loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Codes, not the server's English sentences, as for the password.
  const googleProblem = (err, afterConsent) => {
    if (!err.response) return t('serverUnreachable');
    const data = err.response.data || {};
    if (err.response.status === 429) return tReg('errors.rate_limited', { minutes: waitMinutes(data) });
    switch (data.code) {
      case 'account_inactive':
        return data.pending_approval ? t('google.pendingApproval') : tReg('accountInactive');
      case 'invalid_google_token':
        // A Google credential lasts about an hour, and one held through the
        // consent step can lapse. A new one is a click away.
        return afterConsent ? t('google.errors.expired') : t('google.errors.invalid_google_token');
      case 'google_account_mismatch': return t('google.errors.google_account_mismatch');
      case 'google_address_not_managed': return t('google.errors.google_address_not_managed');
      case 'google_link_on_hold':
        return t('google.errors.google_link_on_hold', {
          until: data.hold_until ? new Date(data.hold_until).toLocaleString() : '',
        });
      case 'google_email_ambiguous': return t('google.errors.google_email_ambiguous');
      case 'google_keys_unavailable': return t('google.errors.google_keys_unavailable');
      case 'google_sign_in_disabled': return t('google.errors.google_sign_in_disabled');
      default: return t('google.errors.generic');
    }
  };

  // What a first Google sign-up asks to be (the register tab's type,
  // affiliation and purpose; routes/google_auth.py), kept with the
  // credential through the consent step.
  const googleRequest = useRef(null);

  const sendGoogle = async (credential, acceptPrivacy, afterConsent = false) => {
    setError('');
    setUnverifiedEmail('');
    setGoogleError('');
    setGoogleBusy(true);
    try {
      const res = await api.post('/auth/google', {
        credential, ...(acceptPrivacy ? { accept_privacy: true } : {}),
        ...(mode === 'register' && googleRequest.current ? googleRequest.current : {}),
      });
      setConsentFor(null);
      if (res.data?.token) enter(res.data.token);
      // A new account waits for a Collection Manager, exactly like one made
      // with a password: no session until then.
      else if (res.data?.pending_approval) setGooglePending(true);
      else setGoogleError(t('google.errors.generic'));
    } catch (err) {
      if (err.response?.data?.code === 'privacy_not_accepted') {
        // Someone new: ask, then send this same credential again.
        setConsentFor(credential);
      } else {
        setConsentFor(null);
        setGoogleError(googleProblem(err, afterConsent));
      }
    } finally {
      setGoogleBusy(false);
    }
  };

  /**
   * "Sign in with Google" and what follows a press of it. On the register
   * tab, the form's own privacy box counts as consent, so the step that asks
   * for it points there instead of drawing a second box.
   */
  const renderGoogle = (registerAccepted = false, request = null) => {
    const consented = registerAccepted || googleAccepted;
    googleRequest.current = request;
    return (
      <>
        <GoogleSignInButton mode={mode} disabled={googleBusy}
                            onCredential={credential => sendGoogle(credential, consented)} />
        {consentFor && (
          <div data-testid="google-consent" role="group" aria-labelledby="google-consent-title"
               style={googleStyles.panel}>
            <p id="google-consent-title" ref={consentRef} tabIndex={-1} style={googleStyles.text}>
              {t('google.consentNeeded')}
            </p>
            {mode === 'register' ? (
              <p style={googleStyles.text}>{t('google.consentAbove')}</p>
            ) : (
              <label style={googleStyles.consent}>
                <input type="checkbox" checked={googleAccepted} data-testid="google-consent-box"
                       onChange={e => setGoogleAccepted(e.target.checked)} title={t('tip.googleConsent')} />
                <span>
                  {tAccount('consentPrefix')}
                  <a href={PRIVACY_POLICY_URL} target={isExternalPolicy() ? '_blank' : undefined}
                     rel="noopener noreferrer" title={t('tip.privacyPolicy')}>{tAccount('privacyPolicy')}</a>
                </span>
              </label>
            )}
            <div style={googleStyles.actions}>
              <button type="button" disabled={!consented || googleBusy} title={t('tip.googleContinue')}
                      onClick={() => sendGoogle(consentFor, true, true)}
                      style={googleStyles.primary(!consented || googleBusy)}>
                {t('google.continue')}
              </button>
              <button type="button" onClick={() => setConsentFor(null)} title={t('tip.googleCancel')}
                      style={googleStyles.secondary}>
                {t('google.cancel')}
              </button>
            </div>
          </div>
        )}
        {googleBusy && <p role="status" style={googleStyles.busy}>{t('google.working')}</p>}
        {googleError && <div role="alert" style={googleStyles.error}>{googleError}</div>}
      </>
    );
  };

  useEffect(() => {
    if (!consentFor) return;
    consentRef.current?.focus?.();
    setGoogleStep(t('google.consentNeeded'));
  }, [consentFor, t]);
  useEffect(() => {
    if (!googlePending) return;
    pendingRef.current?.focus?.();
    setGoogleStep(`${t('google.pendingTitle')}. ${t('google.pendingBody')}`);
  }, [googlePending, t]);

  const tabStyle = active => ({
    flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
    background: active ? 'var(--c-primary)' : 'var(--c-surface)', color: active ? 'var(--c-on-accent)' : 'var(--c-primary-fg)',
  });

  // Already signed in (a bookmark of /login, the back button after signing
  // in): there is nothing to do here.
  if (user) return <Navigate to={target} replace />;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(160deg, var(--c-primary-soft-3) 0%, var(--c-primary-soft-4) 50%, var(--c-primary-soft-5) 100%)',
      padding: 16,
      position: 'relative',
    }}>
      {/* Language toggle */}
      <div style={{
        position: 'absolute', top: 16, insetInlineEnd: 16,
        display: 'flex', border: '1px solid var(--c-border-strong)', borderRadius: 6, overflow: 'hidden',
        background: 'var(--c-surface)',
      }}>
        <button
          onClick={() => toggleLanguage('en')}
          title={t('tip.languageEn')}
          style={{
            background: i18n.language === 'en' ? 'var(--c-primary)' : 'none',
            color: i18n.language === 'en' ? 'var(--c-on-accent)' : 'var(--c-text-body-fg)',
            border: 'none', padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          EN
        </button>
        <button
          onClick={() => toggleLanguage('he')}
          title={t('tip.languageHe')}
          style={{
            background: i18n.language === 'he' ? 'var(--c-primary)' : 'none',
            color: i18n.language === 'he' ? 'var(--c-on-accent)' : 'var(--c-text-body-fg)',
            border: 'none', padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          עב
        </button>
      </div>

      <div style={{
        width: '100%',
        maxWidth: 420,
        background: 'var(--c-surface)',
        borderRadius: 16,
        boxShadow: '0 8px 40px rgba(44, 110, 73, 0.12), 0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}>

        {/* Logo area */}
        <div style={{
          padding: '36px 40px 28px',
          borderBottom: '1px solid var(--c-bg-muted-2)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <LogoMark alt={t('logoAlt')} />
        </div>

        {/* Sign in / Create account */}
        <div role="tablist" style={{ display: 'flex', margin: '20px 40px 0', border: '1px solid var(--c-primary)', borderRadius: 8, overflow: 'hidden' }}>
          <button type="button" role="tab" aria-selected={mode === 'signin'}
                  onClick={() => switchMode('signin')} title={t('tip.signInTab')} style={tabStyle(mode === 'signin')}>
            {tReg('signInTab')}
          </button>
          <button type="button" role="tab" aria-selected={mode === 'register'}
                  onClick={() => switchMode('register')} title={t('tip.registerTab')} style={tabStyle(mode === 'register')}>
            {tReg('registerTab')}
          </button>
        </div>

        <div aria-live="polite" style={srOnly} data-testid="google-step">{googleStep}</div>

        {/* Form area */}
        <div style={{ padding: '24px 40px 36px' }}>
          {googlePending ? (
            <div data-testid="google-pending">
              <h2 ref={pendingRef} tabIndex={-1} style={googleStyles.heading}>{t('google.pendingTitle')}</h2>
              <p style={googleStyles.text}>{t('google.pendingBody')}</p>
              <button type="button" onClick={() => switchMode('signin')} title={tReg('tip.backToSignIn')}
                      style={googleStyles.link}>
                {tReg('backToSignIn')}
              </button>
            </div>
          ) : mode === 'register' ? (
            <RegisterPanel onBackToSignIn={() => switchMode('signin')} onSignedIn={enter}
                           afterForm={({ accepted, request }) => renderGoogle(accepted, request)} />
          ) : (
          <>
          <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--c-text-muted-fg)', textAlign: 'center' }}>
            {t('subtitle')}
          </p>

          <form onSubmit={handleSubmit} data-testid="signin-form">
            <label htmlFor="signin-email" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--c-text-body-fg)', marginBottom: 4 }}>
              {t('email')}
            </label>
            <input
              id="signin-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="your@email.com"
              title={t('tip.email')}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 12px',
                marginBottom: 16,
                border: '1px solid var(--c-border-strong)',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--c-primary)'}
              onBlur={e => e.target.style.borderColor = 'var(--c-border-strong)'}
            />

            <label htmlFor="signin-password" style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--c-text-body-fg)', marginBottom: 4 }}>
              {t('password')}
            </label>
            <div style={{ position: 'relative', marginBottom: 24 }}>
              <input
                id="signin-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                title={t('tip.password')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  paddingInlineEnd: 40,
                  border: '1px solid var(--c-border-strong)',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--c-primary)'}
                onBlur={e => e.target.style.borderColor = 'var(--c-border-strong)'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                title={showPassword ? t('tip.hidePassword') : t('tip.showPassword')}
                aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                style={{
                  position: 'absolute', insetInlineEnd: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
                  color: 'var(--c-text-muted-fg)', padding: 4, lineHeight: 1,
                }}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>

            {/* Under the password box, which is where somebody looks the
                moment the one they typed did not work. */}
            <div style={{ marginTop: -16, marginBottom: 20, textAlign: 'end' }}>
              <Link to="/forgot-password" data-testid="forgot-password-link" title={t('tip.forgotPassword')}
                    style={{ fontSize: 13, color: 'var(--c-primary-fg)', fontWeight: 500 }}>
                {tReset('forgot.linkFromSignIn')}
              </Link>
            </div>

            {error && (
              <div role="alert" style={{
                background: 'var(--c-danger-soft)',
                color: 'var(--c-danger-2-fg)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                marginBottom: 16,
              }}>
                {error}
                {googleHint && (
                  <p data-testid="google-hint" style={{ margin: '8px 0 0', color: 'var(--c-text-body-fg)' }}>
                    {t('googleHint')}{' '}
                    <button type="button" data-testid="google-hint-show" title={t('tip.googleHint')}
                            onClick={() => {
                              googleArea.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
                              setGoogleLit(true);
                              clearTimeout(googleLitTimer.current);
                              googleLitTimer.current = setTimeout(() => setGoogleLit(false), 2400);
                            }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
                                     color: 'var(--c-primary-fg)', fontWeight: 600, textDecoration: 'underline' }}>
                      {t('googleHintShow')}
                    </button>
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              title={t('tip.signIn')}
              style={{
                display: 'block',
                width: '100%',
                padding: '11px',
                background: loading ? 'var(--c-primary-3)' : 'var(--c-primary)',
                color: 'var(--c-on-accent)',
                border: 'none',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
                letterSpacing: 0.3,
              }}
            >
              {loading ? t('signingIn') : t('signIn')}
            </button>
          </form>
          {unverifiedEmail && <ResendVerification email={unverifiedEmail} onSignedIn={enter} />}
          <div ref={googleArea} data-testid="google-area" data-lit={googleLit ? 'true' : 'false'}
               style={googleLit ? { outline: '3px solid var(--c-primary)', outlineOffset: 6, borderRadius: 8 } : undefined}>
            {renderGoogle()}
          </div>
          </>
          )}
        </div>

        {accountDeleted && (
          <div role="status" style={{
            margin: '0 40px 16px', padding: '10px 12px', background: 'var(--c-primary-soft-2)',
            border: '1px solid var(--c-primary-border)', borderRadius: 8, color: 'var(--c-success-3-fg)',
            fontSize: 13, textAlign: 'center',
          }}>
            {tAccount('accountDeleted')}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '14px 40px',
          background: 'var(--c-bg-subtle)',
          borderTop: '1px solid var(--c-bg-muted-2)',
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--c-text-faint-fg)',
        }}>
          {t('footer')}
          <div style={{ marginTop: 6, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={PRIVACY_POLICY_URL} target={isExternalPolicy() ? '_blank' : undefined}
               rel="noopener noreferrer" title={t('tip.privacyPolicy')} style={{ color: 'var(--c-text-muted-fg)' }}>
              {tAccount('privacyPolicy')}
            </a>
            <button type="button" onClick={() => setServerOpen(true)} title={t('tip.serverChange')} style={{
              background: 'none', border: 'none', padding: 0, color: 'var(--c-text-muted-fg)',
              textDecoration: 'underline', cursor: 'pointer', fontSize: 12,
            }}>
              {tAccount('serverChange')}
            </button>
          </div>
        </div>
      </div>
      {serverOpen && (
        <ServerSettings forced={needsServerChoice()} onClose={() => setServerOpen(false)} />
      )}
    </div>
  );
}

const googleStyles = {
  panel: {
    marginTop: 12, padding: '12px 14px', border: '1px solid var(--c-primary-border)',
    borderRadius: 8, background: 'var(--c-primary-soft-2)',
  },
  heading: { margin: '0 0 12px', fontSize: 17, color: 'var(--c-primary-fg)' },
  text: { margin: '0 0 8px', fontSize: 13, color: 'var(--c-text-2-fg)', lineHeight: 1.5 },
  consent: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, margin: '4px 0 12px' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  primary: off => ({
    padding: '8px 14px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: off ? 'var(--c-primary-3)' : 'var(--c-primary)', color: 'var(--c-on-accent)',
    cursor: off ? 'not-allowed' : 'pointer',
  }),
  secondary: {
    padding: '8px 14px', border: '1px solid var(--c-primary)', borderRadius: 8, fontSize: 13,
    background: 'var(--c-surface)', color: 'var(--c-primary-fg)', cursor: 'pointer',
  },
  link: {
    marginTop: 4, background: 'none', border: 'none', padding: 0, color: 'var(--c-primary-fg)',
    textDecoration: 'underline', cursor: 'pointer', fontSize: 13,
  },
  busy: { margin: '10px 0 0', fontSize: 13, color: 'var(--c-text-muted-fg)', textAlign: 'center' },
  error: {
    marginTop: 12, background: 'var(--c-danger-soft)', color: 'var(--c-danger-2-fg)',
    borderRadius: 8, padding: '10px 14px', fontSize: 13,
  },
};
